import moment from "moment";

import { padZero } from "./text-formatter.service";

/**
 * Given objKey, returns a comparator fn to pass into a .sort
 * that will sort objects in the array by date on objKey
 */
export const dateCompareFnFactoryFromKey = (objKey) => (a, b) =>
  DateService.convert(a[objKey]).valueOf() - DateService.convert(b[objKey]).valueOf();

export function formatShortDate(dateValue) {
  const date = new Date(dateValue);
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
}

export const getLastNDates = (n) => {
  const todayDate = new Date();
  const options = { month: "numeric", day: "numeric" };
  const arr = [];
  for (let i = n - 1; i > 0; i -= 1) {
    const date = todayDate - msInAnHour * 24 * i;
    arr.push(new Date(date).toLocaleDateString("en-US", options));
  }
  arr.push("Today");
  return arr;
};

const monthList = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * @param  {int} n
 * @param  {fn} t - translation fn
 * Returns an array of month labels ascending towards the current month
 * If n = 3, and it's January, return ['November', 'December', 'January']
 */
export const getLastNMonths = (n, t = (key) => key) => {
  let monthsLeft = n - 1;
  const lastNMonths = [];
  let currIndex = new Date().getMonth();
  while (monthsLeft >= 0) {
    lastNMonths.unshift(t(`Dates.${monthList[currIndex]}`));
    if (currIndex === 0) {
      currIndex = 11;
    } else {
      currIndex -= 1;
    }
    monthsLeft -= 1;
  }
  return lastNMonths;
};

export const getLastNYears = (n) => {
  let yearsLeft = n - 1;
  const lastNYears = [];
  let currYear = new Date().getFullYear();
  while (yearsLeft >= 0) {
    yearsLeft -= 1;
    lastNYears.unshift(currYear.toString());
    currYear -= 1;
  }
  return lastNYears;
};

export const msInAnHour = 1000 * 60 * 60;

export class DateService {
  constructor({
    hours = 0,
    minutes = 0,
    seconds = 0,
    days = 0,
    months = 0,
    milliseconds = 0,
    value = 0,
  }) {
    this.hours = hours;
    this.minutes = minutes;
    this.seconds = seconds;
    this.day = days;
    this.month = months;
    this.milliseconds = milliseconds;
    this.value = value;
  }

  getHours = () => this.hours;

  getMinutes = () => this.minutes;

  getSeconds = () => this.seconds;

  getDate = () => this.day;

  getMonth = () => this.month;

  valueOf = () => this.value;

  getFullYear = () => NaN;

  toString = () => `${this.hours}:${this.minutes}:${this.seconds}`;

  static validate = (d) => {
    try {
      DateService.convert(d);
      return true;
    } catch (error) {
      return false;
    }
  };

  static convert = (d) => {
    // Converts the date in d to a date-object
    if (moment.isMoment(d)) {
      return d.toDate();
    }
    const date = new Date(d);
    if (Number.isNaN(date.valueOf())) {
      throw new Error(`Invalid date supplied of type: ${typeof d}`);
    }
    return date;
  };

  static compare = (a, b) => {
    // Compare two dates (could be of any type supported by the convert
    // function above) and returns:
    //  -1 : if a < b
    //   0 : if a = b
    //   1 : if a > b
    // NaN : if a or b is an illegal date
    // NOTE: The code inside isFinite does an assignment (=).
    const convertedA = DateService.convert(a).valueOf();
    const convertedB = DateService.convert(b).valueOf();
    if (Number.isFinite(convertedA) && Number.isFinite(convertedB)) {
      return (convertedA > convertedB) - (convertedA < convertedB);
    }
    return NaN;
  };

  static roundEpochTimeToDate = (timestamp) =>
    new Date(new Date(timestamp).toLocaleDateString("en-US")).valueOf();

  static format = (date, style) => {
    if (!date) {
      return "";
    }
    const dateObj = date instanceof DateService ? date : DateService.convert(date);

    // parsing numeric values
    const hours = padZero(dateObj.getHours(), 2);
    const minutes = padZero(dateObj.getMinutes(), 2);
    const seconds = padZero(dateObj.getSeconds(), 2);
    const day = padZero(dateObj.getDate(), 2);
    // Months are indexed starting at 0 (January === 0)
    const month = padZero(dateObj.getMonth() + 1, 2);
    const year = dateObj.getFullYear();

    // parsing word values
    const wordMonth = monthList[dateObj.getMonth()];
    const shortWordMonth = wordMonth.substring(0, 3);

    // some other values
    const totalSeconds = Math.floor(dateObj.valueOf() / 1000);
    const totalMinutes = Math.floor(totalSeconds / 60);
    const totalHours = Math.floor(totalMinutes / 60);
    const remainingSeconds = totalSeconds % 60;

    // reusable
    const hhmmss = `${hours}:${minutes}:${seconds}`;

    switch (style) {
      case "hh:mm:ss MM/DD":
        return `${hhmmss} ${month}/${day}`;
      case "hh:mm:ss":
        return hhmmss;
      case "MM dd":
        return `${wordMonth} ${day}`;
      case "00m 00s":
        return `${padZero(totalMinutes, 2)} M ${padZero(remainingSeconds, 2)} S`;
      case "00h 00m":
        return `${padZero(totalHours, 2)} H ${padZero(totalMinutes % 60, 2)} M`;
      case "00m":
        return `${padZero(totalMinutes, 2)} M`;
      case "Mon DD, YYYY hh:mm:ss":
        if (Number.isNaN(year)) throw new Error("Not a valid Date object");
        return `${shortWordMonth} ${day}, ${year} ${hhmmss}`;
      default:
        return dateObj.toString();
    }
  };

  static timeDifference = (start, end) => {
    const convertedStart = DateService.convert(start).valueOf();
    const convertedEnd = DateService.convert(end).valueOf();
    return DateService.msToDateService(convertedEnd - convertedStart);
  };

  static msToDateService = (ms) => {
    let timeLeft;
    const months = Math.floor(ms / 2628000000);
    timeLeft = ms - months * 2628000000;

    const days = Math.floor(timeLeft / 86400000);
    timeLeft -= days * 86400000;

    const hours = Math.floor(timeLeft / msInAnHour);
    timeLeft -= hours * msInAnHour;

    const minutes = Math.floor(timeLeft / 60000);
    timeLeft -= minutes * 60000;

    const seconds = Math.floor(timeLeft / 1000);
    timeLeft -= seconds * 1000;

    return new DateService({
      months,
      days,
      hours,
      minutes,
      seconds,
      milliseconds: timeLeft,
      value: ms,
    });
  };

  static contains = (start, end, timestampBeingChecked) => {
    try {
      const startTime = DateService.convert(start).valueOf();
      const endTime = DateService.convert(end).valueOf();
      const checkTime = DateService.convert(timestampBeingChecked).valueOf();
      return startTime < checkTime && checkTime < endTime;
    } catch (e) {
      return false;
    }
  };
}
